// Program data for DC Early Learning Collaborative

export const dcMenInECE = {
  title: "DC Men In ECE",
  subtitle: "Fostering increased representation of male educators in Early Childhood Education",
  heroImage: "https://customer-assets.emergentagent.com/job_early-learn-dc/artifacts/hz9p9akd__JNS3976DC%20Men%20conference_2023%20%281%29.jpg",
  logo: "https://customer-assets.emergentagent.com/job_early-learn-dc/artifacts/fxxyt2rj_Untitled%20%28Card%20%28Landscape%29%29.png",
  intro: `Since launching in April 2022 during the national "Month of the Young Child," DC Men in Early Childhood Education (DC Men in ECE) has become a catalyst for change in Washington, D.C.'s early childhood education (ECE) landscape. From an initial networking reception of 40 men to hosting a city-wide conference with over 200 registrants both men and women in October 2023, the organization has cultivated momentum toward a singular mission: Empowering men of color in ECE by enhancing their recruitment, retention, and advancement through community support, professional development, and shared resources.`,
  stats: [
    { value: "3.3%", label: "Men in national ECE workforce" },
    { value: "<1%", label: "Men of color in ECE" },
    { value: "90-100", label: "Target recruitment goal" },
    { value: "3,000+", label: "DC ECE workforce members" }
  ],
  mission: {
    title: "DC Men in ECE Mission",
    description: "To foster interdisciplinary induction and networks among men, creating meaningful career opportunities and experiences for young men and empowering them to drive the industry's progress."
  },
  impact: {
    title: "Why This Matters",
    description: "DC Men in ECE is driven by the belief that representation shapes educational identity and outcomes. By expanding opportunities for Black and Brown men in early childhood education, the organization seeks to:",
    points: [
      "Cultivate inclusive leadership pipelines that mirror the community's demographics",
      "Improve educational and life outcomes for children of color",
      "Foster a more diverse, relational, and effective ECE workforce"
    ]
  },
  benefits: {
    title: "Benefits to the Broader Workforce",
    points: [
      "Reducing gender-based occupational segregation that limits career mobility and wage equity",
      "Promoting professional collaboration across genders, leading to more dynamic, inclusive, and creative teaching environments"
    ]
  },
  objectives: [
    {
      title: "Improve Teaching and Learning Practices",
      description: "Strengthen pedagogical knowledge and culturally responsive teaching.",
      activities: [
        "Formal Networking System: Establish quarterly convenings for male educators to exchange ideas, build solidarity, and share professional interests",
        "Mentorship Program: Pair early-career teachers with seasoned educators and administrators to support career growth, goal setting, and workplace navigation"
      ]
    },
    {
      title: "Advance Self-Development Skills",
      description: "Support confidence, emotional intelligence, and leadership among men educators.",
      activities: [
        "Leadership and Research Forums: Convene thought leaders for discussions on gender diversity, workforce equity, and excellence in early childhood education",
        "Professional Development Calendar: Host quarterly skill- and goal-based sessions focused on best practices, policy advocacy, and well-being"
      ]
    },
    {
      title: "Enhance Career Mobility Opportunities",
      description: "Provide mentorship and pathways toward advancement, certification, and leadership roles.",
      activities: [
        "Recruitment and Retention Plan: Develop a systematic process for outreach, tracking, and recognition, including an annual acknowledgment ceremony",
        "Quarterly Publication: Launch a digital newsletter for professional tips, events, resources, and member spotlights"
      ]
    }
  ],
  conference: {
    title: "Annual DC Men in ECE Conference",
    description: "The cornerstone event bringing together educators, policymakers, and community leaders to share research, best practices, and innovations. The conference amplifies gender equity, highlights women educators as collaborators, and encourages men to challenge biases and assumptions that perpetuate inequality."
  },
  outcomes: {
    title: "Evaluation and Outcomes",
    description: "Progress will be tracked through measurable indicators:",
    metrics: [
      "Recruitment of at least 90 Black and Brown men by end of 2027",
      "A 70% retention rate among first-year participants",
      "50% of members advancing toward professional credentials (CDA, AA, or BA)",
      "Increased presence of men in city-wide early childhood policy forums"
    ],
    methods: "Evaluation methods will include pre/post surveys, retention tracking, and qualitative interviews documenting participant impact stories."
  },
  partners: [
    "OSSE",
    "DCAEYC",
    "Educare DC",
    "Southeast Children's Fund (DC LEAD)",
    "University of the District of Columbia",
    "Council for Professional Recognition",
    "Hopkins House"
  ],
  closing: "DC Men in ECE will continue to shift the narrative of who teaches and leads in early learning spaces. This initiative directly aligns with D.C.'s equity commitments and the national movement toward inclusive, high-quality education for all children. Support for DC Men in ECE is an investment in representation, resilience, and the transformational power of early childhood education."
};

export const alaaProgram = {
  title: "Maurice Sykes Emerging Leaders Program",
  subtitle: "Igniting Leadership Excellence in Early Childhood Education",
  heroImage: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg",
  intro: `The Maurice Sykes Emerging Leaders Program is a groundbreaking initiative sponsored by the DC Early Learning Collaborative (DCELC) that empowers early childhood directors in the District of Columbia to make a significant impact in their communities. This innovative professional development program is tailor-made for leaders in early childhood education who are committed to driving positive change for young children, their families, and educators.`,
  whyItMatters: {
    title: "Why Emerging Leaders Matters",
    description: `Early Childhood Education ("ECE") plays a critical role in shaping the future of our society. The Maurice Sykes Emerging Leaders Program stands out as a beacon for transformative leadership and advocacy. Participants will embark on a six-month immersive journey designed to develop visionary leaders who can disrupt the status quo, advocate for high-quality educational programs, and dismantle systemic inequities within the ECE sector. By joining the program, participants will engage in a rich curriculum that emphasizes quality enhancement, community engagement, and a research-driven approach to leadership development.`
  },
  features: [
    {
      title: "Transformative Leadership Development",
      description: "Equip participants with the essential knowledge and skills to become a progressive leader in early childhood education."
    },
    {
      title: "Advocacy for Excellence",
      description: "Learn to champion children's rights to equitable access to high-quality education."
    },
    {
      title: "Real-World Challenges",
      description: "Participants tackle practical leadership scenarios, emerging stronger and more resilient as they apply knowledge in real-time."
    },
    {
      title: "Capstone Leadership Project",
      description: "Participants synthesize learning into an impactful project that drives systemic change within their organization and community."
    }
  ],
  cohort: {
    title: "Cohort Experience",
    description: "The inaugural cohort was limited to 25 dedicated ECE leaders from diverse backgrounds across the District. We found that the exclusive setting fostered deep connections, collaboration, and rich discussions that elevated the collective learning experience. The program's curriculum was designed to inspire motivation and engagement among participants, foster a culture of enthusiasm and commitment in their respective organizations.",
    size: 25,
    duration: "6 months"
  },
  promoVideo: "#"
};

export const infantToddlerInstitute = {
  title: "Quality Improvement Institute for Infant and Toddler Care",
  subtitle: "Advancing the Early Childhood Education Profession in the District of Columbia",
  heroImage: "https://images.pexels.com/photos/8613089/pexels-photo-8613089.jpeg",
  intro: `The DCELC Infant Toddler Program seeks to build a strong, credentialed, and respected early childhood education (ECE) workforce that reflects the excellence and diversity of the communities it serves.`,
  achievements: [
    "Expanded the Tuition Assistance Program (TAP) to include infant–toddler educators",
    "Launched the first infant–toddler educator cohort in Wards 7 and 8",
    "Secured an additional $850,000 annually in workforce scholarships for UDC students since 2021",
    "Convened citywide Faculty Institutes that certified educators and coaches using the WestEd Program for Infant and Toddler Care (PITC) model"
  ],
  partnership: "University of the District of Columbia Community College (UDC-CC)",
  goals: {
    title: "Program Goals",
    description: "DCELC seeks to strengthen both the infant–toddler educator pipeline by recruiting and preparing 200 new infant–toddler educators, advance faculty and coaching capacity through PITC."
  },
  training: {
    title: "Three-Day Intensive Training",
    description: "This three-day intensive training provides participants with a deep understanding of the foundations of infant/toddler development, the PITC philosophy, and evidence-based practices that promote responsive, relationship-centered caregiving.",
    approach: "The PITC approach—grounded in relationship-based care and cultural responsiveness—aligns with DCELC's vision of equity-driven, developmentally informed early learning systems."
  },
  localLeaders: {
    title: "By bringing PITC directly to DC's community of practice, our program ensures that local ECE leaders:",
    points: [
      "Deepen their understanding of infant/toddler developmental milestones and trajectories",
      "Develop proficiency in coaching educators to observe, interpret, and respond sensitively to infants' cues",
      "Strengthen program-wide capacity to implement relationship-based, culturally grounded, and inclusive practices"
    ]
  },
  rationale: {
    title: "Program Rationale",
    description: "Research confirms that the earliest years of life form the foundation for lifelong learning, behavior, and health. Responsive and knowledgeable educators are central to creating nurturing environments that support brain development, emotional regulation, and emerging language and motor skills. However, consistent, high-quality coaching and professional development are needed to sustain these practices across diverse early childhood programs."
  },
  participants: {
    title: "Participant Profile",
    count: "10–30 participants",
    description: "Intended for program coaches, ECE leaders, technical assistance providers, and infant/toddler specialists working across DC's mixed-delivery system."
  },
  outcomes: {
    title: "Expected Outcomes",
    description: "By the conclusion of the Program, participants will:",
    points: [
      "Demonstrate expanded knowledge of infant and toddler developmental foundations",
      "Apply PITC principles to enhance coaching conversations and instructional support",
      "Develop a personalized coaching framework grounded in evidence-based practice",
      "Strengthen program-level implementation of relationship-based and culturally responsive care"
    ]
  },
  promoVideo: "https://next.frame.io/share/0d41713f-b23a-42f0-8eb2-12a231e0c881/view/247ff946-17a8-4f8d-b89e-654d2ba8ecff"
};


export const ecePublicGoodCampaign = {
  title: "ECE as a Public Good Campaign",
  subtitle: "Reframing Early Childhood Education as Essential to Washington, DC's Future",
  heroImage: "https://customer-assets.emergentagent.com/job_early-learn-dc/artifacts/a97z5czf_2140908966.jpg",
  secondaryImage: "https://customer-assets.emergentagent.com/job_early-learn-dc/artifacts/hsbkyozn_2140907358.jpg",
  intro: `DCELC will conduct citywide communications and awareness campaigns designed to shape how families, business leaders, and policymakers understand and value early learning. These campaigns will be designed to go beyond messaging—they are strategic efforts to reframe early childhood education as an essential public good and a cornerstone of the District's social and economic well-being.`,
  approach: {
    title: "Our Strategic Approach",
    description: "Through storytelling, data-driven narratives, and culturally responsive outreach, DCELC highlights the impact of high-quality early education on children's development, family stability, and community prosperity. By uplifting the voices of educators, parents, and early learning advocates, DCELC creates compelling narratives that inspire public investment, influence policy decisions, and mobilize collective action in support of young children and the workforce that serves them."
  },
  whyItMatters: {
    title: "Why ECE as a Public Good Matters",
    points: [
      {
        title: "Economic Driver",
        description: "Every $1 invested in quality early childhood education returns $7-$13 to the economy through increased productivity, reduced social costs, and higher lifetime earnings."
      },
      {
        title: "Workforce Enabler",
        description: "Reliable, high-quality childcare enables parents—especially mothers—to participate fully in the workforce, driving economic growth and family stability."
      },
      {
        title: "Foundation for Success",
        description: "90% of brain development occurs before age 5. Quality early education sets children on a trajectory for academic achievement and lifelong success."
      },
      {
        title: "Equity Imperative",
        description: "Access to quality ECE helps close opportunity gaps, ensuring every child regardless of zip code has the chance to thrive."
      }
    ]
  },
  strategies: [
    {
      title: "Public Messaging",
      description: "Frame ECE as not just a childcare solution but a driver of economic growth, workforce participation, and long-term child wellbeing.",
      icon: "megaphone"
    },
    {
      title: "Media Strategy",
      description: "Partner with local and regional press, amplify stories of ECE providers, and highlight the impact of adequate funding.",
      icon: "newspaper"
    },
    {
      title: "Digital Engagement",
      description: "Leverage social media, newsletters, and web platforms to educate diverse audiences on ECE's role in community prosperity.",
      icon: "share"
    },
    {
      title: "Business & Economic Case",
      description: "Produce fact sheets, infographics, and policy briefs showcasing the return on investment (ROI) of ECE for DC's economy.",
      icon: "chart"
    },
    {
      title: "Public Will Building Events",
      description: "Host town halls, advocacy days on Capitol Hill/at the DC Council, and ECE awareness fairs.",
      icon: "calendar"
    },
    {
      title: "Storytelling Campaigns",
      description: "Elevate personal narratives from families, educators, and community providers to humanize policy debates and broaden public empathy.",
      icon: "heart"
    }
  ],
  impact: {
    title: "The Case for Investment",
    stats: [
      { value: "$7-$13", label: "Return on every $1 invested in ECE" },
      { value: "90%", label: "Brain development before age 5" },
      { value: "57%", label: "DC students who are Black" },
      { value: "19%", label: "DC students who are Hispanic" }
    ]
  },
  callToAction: {
    title: "Join the Movement",
    description: "The future of Washington, DC depends on how we invest in our youngest residents today. Join DCELC in advocating for early childhood education as a fundamental public good—essential to our children, our families, our economy, and our community.",
    actions: [
      "Attend an advocacy event or town hall",
      "Share ECE stories on social media",
      "Contact your DC Council member",
      "Partner with DCELC on awareness campaigns"
    ]
  },
  research: {
    title: "Research & Evidence",
    points: [
      "Nobel Prize-winning economist James Heckman's research demonstrates that high-quality early childhood programs for disadvantaged children can deliver a 13% annual return on investment.",
      "The National Institute for Early Education Research (NIEER) finds that children who attend quality pre-K programs score higher on measures of cognitive and social-emotional development.",
      "According to the Economic Policy Institute, the childcare industry supports approximately 3.5 million jobs nationally and contributes $99 billion annually to the U.S. economy.",
      "Research from the Center on the Developing Child at Harvard University shows that early experiences literally shape brain architecture, establishing either strong or weak foundations for future learning and health."
    ]
  }
};

